import { MetricCard } from "./MetricCard.jsx";
import { HorizontalBar } from "./HorizontalBar.jsx";
import { HourlyChart } from "./HourlyChart.jsx";

const C = { bg: "#111", border: "#1e1e1e", accent: "#e8700a", text: "#f5f5f5", dim: "#666", green: "#22c55e", red: "#ef4444" };
const GAP = 8;

function fmt(n, currency) {
  if (n == null) return "—";
  const sym = currency === "USD" ? "US$" : "$";
  if (n >= 1000000) return `${sym}${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${sym}${(n / 1000).toFixed(0)}k`;
  return `${sym}${Math.round(n).toLocaleString("es-AR")}`;
}

function Card({ children, style }) {
  return <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px", ...style }}>{children}</div>;
}

function CardTitle({ children }) {
  return <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", color: C.dim, textTransform: "uppercase", marginBottom: 12 }}>{children}</div>;
}

function VarChip({ val }) {
  if (val == null) return <span style={{ color: C.dim, fontSize: 11 }}>nuevo</span>;
  const up = val >= 0;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: up ? C.green : C.red }}>
      {up ? "▲" : "▼"} {Math.abs(val)}%
    </span>
  );
}

function SkeletonRow({ cols, h = 90 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: GAP }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} style={{ height: h, background: "#161616", borderRadius: 10, animation: "pulse 1.5s ease-in-out infinite" }} />
      ))}
    </div>
  );
}

export function OperacionesView({ summary, salesByHour, topProducts, currency, loading }) {
  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", gap: GAP }}>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      <SkeletonRow cols={4} /><SkeletonRow cols={3} h={180} /><SkeletonRow cols={2} h={240} />
    </div>
  );

  const s = summary || {};
  const turnos = s.ventas_por_turno || {};
  const products = topProducts || [];
  const hours = salesByHour || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: GAP }}>

      {/* Fila 1: 4 metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: GAP }}>
        <MetricCard label="Ventas netas" value={fmt(s.facturacion_total, currency)} />
        <MetricCard label="Ticket promedio" value={fmt(s.ticket_promedio, currency)} />
        <MetricCard label="Transacciones" value={s.transacciones ?? "—"} />
        <MetricCard label="Margen" value={s.margen_pct != null ? `${s.margen_pct}%` : "—"} />
      </div>

      {/* Fila 2: Barras por hora (2/3) + Turnos (1/3) */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: GAP }}>
        <Card>
          <CardTitle>Ventas por hora</CardTitle>
          <HourlyChart data={hours} currency={currency} />
        </Card>
        <Card>
          <CardTitle>Ventas por turno</CardTitle>
          {Object.values(turnos).length > 0 ? (
            <HorizontalBar
              items={Object.values(turnos).map(t => ({ label: t.label, value: t.total }))}
              unit={currency === "USD" ? "US$" : "$"}
              showPct={false}
            />
          ) : <div style={{ color: C.dim, fontSize: 12 }}>Sin datos de turnos</div>}
        </Card>
      </div>

      {/* Fila 3: Top productos barras (izq) + Tabla variación (der) */}
      {products.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: GAP }}>
          <Card>
            <CardTitle>Top productos — unidades</CardTitle>
            <HorizontalBar
              items={products.slice(0, 8).map(p => ({ label: p.producto, value: p.unidades }))}
              showPct={false}
            />
          </Card>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "16px 18px 0", fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", color: C.dim, textTransform: "uppercase", marginBottom: 0 }}>
              Top productos — variación
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Producto", "Und", currency === "USD" ? "Revenue" : null, "vs anterior"].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: h === "Producto" ? "left" : "right", color: C.dim, fontWeight: 600, fontSize: 10 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.slice(0, 10).map((p, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : "#0d0d0d" }}>
                    <td style={{ padding: "8px 12px", color: C.text, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.producto}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: C.dim }}>{p.unidades}</td>
                    {currency === "USD" && p.revenue != null && (
                      <td style={{ padding: "8px 12px", textAlign: "right", color: C.text }}>US${p.revenue.toLocaleString("en-US")}</td>
                    )}
                    <td style={{ padding: "8px 12px", textAlign: "right" }}><VarChip val={p.variacion_pct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

    </div>
  );
}
